import React, {useRef, useState} from 'react';
import {ArrowRight, Check, LoaderCircle, Monitor, Plus} from 'lucide-react';
import {useLang} from './i18n';
import './account-switcher.css';

export type SavedAccount = {id: string; displayName: string; username: string; avatar?: string | null};

type Props = {
  accounts: SavedAccount[];
  currentId: string;
  renderAvatar: (account: SavedAccount) => React.ReactNode;
  request: (path: string, method: 'POST', body: Record<string, string>) => Promise<unknown>;
  onComplete: () => void;
  loading?:boolean;
  loadError?:string;
  onRetry?:()=>Promise<void>;
};

export function AccountSwitcher({accounts, currentId, renderAvatar, request, onComplete,loading=false,loadError='',onRetry}: Props) {
  const [,,tr] = useLang();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState('');
  const inFlight = useRef(false);

  async function select(account?: SavedAccount) {
    if (inFlight.current || account?.id === currentId) return;
    inFlight.current = true;
    setPending(account?.id ?? 'add');
    setError('');
    try {
      await request(account ? '/auth/accounts/select' : '/auth/accounts/add', 'POST', account ? {userId: account.id} : {});
      onComplete();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : tr('The request failed. Please try again.', 'Die Anfrage ist fehlgeschlagen. Bitte versuche es erneut.'));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }

  return <div className="account-switcher">
    <header className="account-switcher-header">
      <h2 id="account-switcher-title">{tr('Switch Account', 'Account wechseln')}</h2>
      <p>{tr('Choose the account you want to chat with.', 'Wähle den Account, mit dem du chatten möchtest.')}</p>
    </header>
    {loading&&<p role="status" className="account-switcher-loading"><LoaderCircle className="spin" size={16}/>{tr('Loading saved accounts…','Gespeicherte Accounts werden geladen …')}</p>}
    {loadError&&<div className="account-switcher-error" role="alert">{loadError}<button type="button" className="secondary" disabled={loading} onClick={()=>void onRetry?.()}>{tr('Try again','Erneut versuchen')}</button></div>}
    <ul className="account-switcher-accounts" aria-label={tr('Saved accounts', 'Gespeicherte Accounts')} aria-busy={pending !== null||loading}>
      {accounts.map(account => {
        const current = account.id === currentId;
        const switching = pending === account.id;
        return <li key={account.id}>
          <button type="button" className={'account-switcher-account' + (current ? ' is-current' : '')}
            disabled={pending !== null || current||loading} aria-current={current ? 'true' : undefined}
            onClick={() => void select(account)}>
            {renderAvatar(account)}
            <span className="account-switcher-identity">
              <strong>{account.displayName}</strong>
              <span>@{account.username}</span>
            </span>
            {current ? <span className="account-switcher-current"><Check size={13} aria-hidden="true"/>{tr('Active', 'Aktiv')}</span>
              : switching ? <LoaderCircle className="spin" size={18} aria-hidden="true"/>
              : <ArrowRight className="account-switcher-arrow" size={18} aria-hidden="true"/>}
          </button>
        </li>;
      })}
    </ul>
    {accounts.length === 0 && !loading && !loadError && <p className="account-switcher-empty">{tr('No saved accounts yet. Add an account to switch between profiles here.', 'Noch keine Accounts gespeichert. Füge einen Account hinzu, um hier zwischen Profilen zu wechseln.')}</p>}
    <button type="button" className="account-switcher-add" disabled={pending !== null||loading} onClick={() => void select()}>
      <span className="account-switcher-add-icon">{pending === 'add' ? <LoaderCircle className="spin" size={20} aria-hidden="true"/> : <Plus size={20} aria-hidden="true"/>}</span>
      <span className="account-switcher-identity"><strong>{tr('Add Account', 'Account hinzufügen')}</strong><span>{tr('Sign in with another account', 'Mit einem weiteren Account anmelden')}</span></span>
    </button>
    <span className="account-switcher-status" role="status">{pending && (pending === 'add' ? tr('Opening sign-in…', 'Anmeldung wird geöffnet …') : tr('Switching account…', 'Account wird gewechselt …'))}</span>
    {error && <p className="account-switcher-error" role="alert">{tr('Account action failed.', 'Account-Aktion fehlgeschlagen.')} {error}</p>}
    <footer className="account-switcher-footer"><Monitor size={16} aria-hidden="true"/><p>{tr('Only accounts signed in on this device appear here.', 'Hier erscheinen nur Accounts, die auf diesem Gerät angemeldet wurden.')}</p></footer>
  </div>;
}
